import { autoinject } from 'aurelia-framework';
import { bindable } from 'aurelia-typed-observable-plugin';
import { task } from 'app/shared/lib/decorators';
import { CustomElementBase } from 'app/shared/elements/custom-element-base';
import { OpenFlags } from 'app/shared/flags/open-flags';
import { fireEventOn } from 'app/shared/lib/fire-events';
import { getKey, Key } from 'app/shared/lib/keys';
import { canReceiveFocus, getFocusableElements, IReturnFocus, returnFocus, getFocusSelector } from 'app/shared/lib/focus';
import { guid } from 'app/shared/lib/guid';

export interface IPopup {
    state: OpenFlags;
    open(preferredPriorFocus?: HTMLElement): void;
    close(showConfirm?: boolean): void;
}

export type PopupEvent = CustomEvent<never>;

@autoinject()
export class PopupBase extends CustomElementBase implements IPopup {

    public state: OpenFlags = OpenFlags.Closed;

    protected id: guid = guid();

    protected $container: HTMLElement = this.element;  //The container element for the popup content, which may not be the same as the component's top-level element.
    protected $priorFocused: HTMLElement | null = null;  //The element that had focus prior to the popup opening.
    protected $priorFocusPath: IReturnFocus | null = null; //The focus path of the priorFocusedElement

    @bindable.stringNull()
    protected labelledbyId: string | null = null;  //An element's `id`. Sets the popup's `aria-labelledby`

    @bindable.stringNull()
    protected describedbyId: string | null = null;  //An element's `id`. Sets the popup's `aria-describedby`

    protected attached() {
        document.addEventListener('mousedown', this.handleMousedown as EventListener);
        this.element.addEventListener('keydown', this.handleKeydown as EventListener);
        this.element.addEventListener('transitionstart', this.handleTransitionstart as EventListener);
        this.element.addEventListener('transitionend', this.handleTransitionend as EventListener);
    }

    protected detached() {
        document.removeEventListener('mousedown', this.handleMousedown as EventListener);
        this.element.removeEventListener('keydown', this.handleKeydown as EventListener);
        this.element.removeEventListener('transitionstart', this.handleTransitionstart as EventListener);
        this.element.removeEventListener('transitionend', this.handleTransitionend as EventListener);
    }

    protected handleMousedown = (event: MouseEvent) => {
        if((this.state & OpenFlags.Open) !== 0 && this.shouldDismiss(event)) {
            this.close();
        }
    };

    protected handleKeydown = (event: KeyboardEvent) => {
        if((this.state & OpenFlags.Open) !== 0 && this.shouldDismiss(event)) {
            const cancelButtons = Array.from(this.$container.querySelectorAll('ess-button[type="cancel"],ess-button[type="close"]')) as HTMLElement[];

            //click the last cancel button within the modal. This is needed for modals that can open additional modals.
            if(cancelButtons.length > 0) {
                cancelButtons[cancelButtons.length - 1].click();
            } else this.close();

            event.stopPropagation();
        }
    };

    protected handleTransitionstart = ({ target }: TransitionEvent) => {
        if(target === this.element) {
            this.state |= OpenFlags.Transitioning;
        }
    };

    protected handleTransitionend = ({ target }: TransitionEvent) => {
        if(target === this.element) {
            this.state &= ~OpenFlags.Transitioning;

            if(this.state & OpenFlags.Open) {
                this.focusPopup();
            }

            fireEventOn(this.element, this.state & OpenFlags.Open ? 'open' : 'close');
        }
    };

    public open(preferredPriorFocus?: HTMLElement) {
        if(this.state & OpenFlags.Closed && fireEventOn(this.element, 'opening')) {
            this.state = OpenFlags.Opening;
            this.$priorFocused = preferredPriorFocus ?? document.activeElement as HTMLElement;
            this.$priorFocusPath = getFocusSelector(this.$priorFocused);
        }
    }

    public close() {
        if(this.state & OpenFlags.Open && fireEventOn(this.element, 'closing')) {
            this.state = OpenFlags.Closing;
            this.focusPrior();
        }
    }

    /**
     * Move focus to the popup's first element that can receive focus based on priority:
     *   1) autofocus
     *   2) focusable by tabindex and DOM order (excluding tabindex = -1 and disabled)
     *   3) popup container (as a fallback)
     */
    @task
    protected focusPopup() {
        let focusElement: HTMLElement | undefined;

        // Get elements that are focusable, ordered by tabindex, and tabbable (don't have tabindex = -1 or are disabled).
        const focusable = getFocusableElements(this.element).filter(element => canReceiveFocus(element));

        // Look for the first [autofocus] element
        focusElement = focusable.find(element => element.hasAttribute('autofocus'));

        // Otherwise, look for the first focusable element
        if(!focusElement && focusable.length > 0) { focusElement = focusable[0]; }

        // Otherwise, focus on the container.  The inherited component view should ensure the element is focusable
        if(!focusElement) { focusElement = this.$container; }

        focusElement.focus();
        const SCROLLABLE_CONTAINER_SELECTOR = '.ess-dialog-content-wrapper';
        const scrollContainer = focusElement.closest(SCROLLABLE_CONTAINER_SELECTOR);
        if(scrollContainer) scrollContainer.scrollTo(0, 0);
    }

    /**
     * Focus on the element that previously had focus, when the popup is closed.
     */
    protected focusPrior() {
        if(canReceiveFocus(this.$priorFocused as Element) && this.$priorFocused) {
            this.$priorFocused.focus()
        } else if(this.$priorFocusPath) {
            returnFocus(this.$priorFocusPath);
        }
        this.$priorFocused = null;
        this.$priorFocusPath = null;
    }

    /**
     * Determine whether an Event (Mouse or Keyboard) is able to dismiss(close) the popup.
     * @param event
     * @returns
     */
    protected shouldDismiss(event: Event): boolean {
        if(event instanceof KeyboardEvent) {
            return getKey(event) === Key.Esc;
        }
        if(event instanceof MouseEvent) {
            const target = event.target as Element;
            const container = target.closest('.ess-popup-container');

            if(container) {
                // don't dismiss clicks within another popup container (another modal)
                if(!this.element.contains(container)) {
                    return false;
                }
            } else {
                // don't dismiss flatpickr clicks that happen inside the external flatpickr element
                const flatpickrElements = Array.from<HTMLElement>(document.querySelectorAll('body > [class^="flatpickr-"]'));
                if(flatpickrElements.some((element) => element.contains(target))) {
                    return false;
                }

                // don't dismiss clicks within the toast container
                const toastContainer = target.closest('#toast-container');
                if(!this.element.contains(toastContainer)) {
                    return false;
                }
            }

            return !this.element.contains(target);
        }
        return false;
    }
}
